import React from 'react'
import { Page } from '../Elements/PageMargins'
import TitleContext from '../../TitleContext'
import { useTranslation } from 'react-i18next'
import DynamicBreadcrumbs from '../Elements/DynamicBreadcrumbs'
import { useLocation, useNavigate, useParams } from 'react-router'
import Box from '@mui/material/Box'
import CardMedia from '@mui/material/CardMedia'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import Paper from '@mui/material/Paper'
import ContainedButton, {
  ContainedButtonVariant,
} from '../Buttons/ContainedButton'
import TextButton, { TextButtonVariant } from '../Buttons/TextButton'
import Header from '../Elements/Header'
import { contentApi, extractedErrorObject, users } from '../../api/swagger'
import { useTheme } from '@mui/material'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'
import useEndpoint from '../../hooks/useEndpoint'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { useMountEffect } from '../../hooks/useMountEffect'

const NDALink = process.env.REACT_APP_NDA_URL ?? ''
const moduleConfig = {
  business: '/business',
  learning: '/learning',
}
type ModuleKey = keyof typeof moduleConfig
export const ContentNDA: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const title = t('Business.NonDisclosureAgreement', 'Non-Disclosure Agreement')
  const navigate = useNavigate()
  const location = useLocation()
  const basePath = location.pathname.split('/')[1] as ModuleKey
  const { useTitleEffect } = React.useContext(TitleContext)
  useTitleEffect(title)
  const { assetKey } = useParams<{ assetKey: string }>()
  const { handleError, handleMessage } = useAppSnackbar()
  const theme = useTheme()
  const navigateToModule = () => navigate({ pathname: moduleConfig[basePath] })
  const navigateToAssetContent = () =>
    navigate(
      { pathname: `${moduleConfig[basePath]}/${basePath}-content/${assetKey}` },
      {
        replace: true,
      }
    )
  const handleAccept = async () => {
    try {
      await contentApi.ndaAcknowledgementPath({
        body: {
          assetKey: +`${assetKey}`,
          ndaAcknowledged: true,
        },
      })
      handleMessage(
        t(
          'Business.NonDisclosureAgreement.Agreed',
          'You have successfully agreed to the NDA.'
        )
      )

      navigateToAssetContent()
    } catch (error) {
      await handleError(
        error,
        t(
          'Business.NonDisclosureAgreement.Error',
          'Something went wrong accepting NDA.'
        )
      )
    }
  }

  const handleCancel = () => {
    navigateToModule()
  }

  const {
    BusinessContent: { fetchUserNdaStatus: fetchUserNdaStatusLoadingId },
  } = useLoadingIds()

  const { triggerFetch: checkNdaStatus } = useEndpoint({
    loadingId: fetchUserNdaStatusLoadingId,
    swaggerCall: async () => {
      try {
        const { hasSignedNda, assetRequiresNda } =
          await users.fetchUserNdaStatus({
            assetKey: parseInt(assetKey as string),
          })

        if (!assetRequiresNda) {
          navigateToAssetContent()
        }
        if (assetRequiresNda && hasSignedNda) {
          navigateToAssetContent()
        }
      } catch (err) {
        const errorObj = await extractedErrorObject(err)

        if (errorObj?.code == 'Not Found') {
          navigate(
            {
              pathname: '/business',
            },
            {
              replace: true,
            }
          )
        }
        if (errorObj?.code == 'Forbidden') {
          navigate(
            {
              pathname: '/Account/profile',
            },
            {
              replace: true,
            }
          )
        }
        throw new Error(errorObj?.message)
      }
    },
  })

  useMountEffect(checkNdaStatus)

  return (
    <Page>
      <DynamicBreadcrumbs
        breadcrumbs={[
          {
            label:
              basePath === 'business'
                ? t('Business.Content.BreadCrumb', 'Business')
                : t('Learning.Content.BreadCrumb', 'Learning Center'),
            onClick: navigateToModule,
          },
          {
            label: t(
              'Business.Content.BreadCrumb.NonDisclosureAgreement',
              'Non-Disclosure Agreement'
            ),
          },
        ]}
      />
      <Paper
        sx={{
          padding: theme.spacing(0, 4, 4),
        }}
      >
        <Box
          paddingBottom={{ xs: 3, sm: 3, md: 0 }}
          display="flex"
          justifyContent="space-between"
          flexDirection={useShowOnDesktop() ? 'row' : 'column'}
          alignItems="center"
        >
          <Header
            id="learningPathHeader"
            headerName={t(
              'Business.Content.NonDisclosureAgreement.Instructions',
              'Please read and accept this non-disclosure agreement to continue'
            )}
          />
          <Box
            display="flex"
            width={useShowOnDesktop() ? 'auto' : '100%'}
            justifyContent={!useShowOnDesktop() ? 'space-around' : 'flex-start'}
          >
            <TextButton
              id="cancel"
              variant={TextButtonVariant.Cancel}
              onClick={handleCancel}
            />
            <ContainedButton
              id="primary"
              variant={ContainedButtonVariant.Accept}
              onClick={handleAccept}
            />
          </Box>
        </Box>
        <Box display="flex" flex="1" height={!useShowOnDesktop ? 250 : 600}>
          <CardMedia
            component="iframe"
            src={NDALink}
            allowFullScreen
            title="Non-Disclosure Agreement"
          />
        </Box>
      </Paper>
    </Page>
  )
}

export default ContentNDA
